import type { AttemptProvenance } from '@/lib/coaching/problem-history-query'
import type { ViaAttribution } from '@/lib/problem-equivalence'
import type { SourceOption } from './source-option'

export type CurriculumItemType =
  | 'coding'
  | 'sd'
  | 'behavioral'
  | 'interview'
  | 'other'

export type Difficulty = 'Easy' | 'Med' | 'Hard' | '—'

/** One line of the Topic / Sub column: a topic and one of its subtopics. */
export interface TopicPair {
  topic: string
  subtopic: string
  /**
   * The problem's primary pair, decided server-side (see the DTO in
   * `lib/coaching/problem-taxonomy.ts`): the first pair of its first
   * approach. At most one pair per row carries it, and a row whose first
   * approach is untagged carries it on none — that row has no primary pair,
   * even though its later pairs are real taxonomy.
   *
   * Select the primary pair through this flag, never through pair 0
   * (AGENTS.md: no semantic meaning from array position) — pair 0 is exactly
   * what silently promotes a later approach on such a row.
   */
  isPrimary: boolean
}

export interface LibraryItem {
  id: string
  title: string
  type: CurriculumItemType
  /**
   * The primary pair, flattened — what surfaces with room for one line show:
   * the link-question modal's search and summary, and the card meta line.
   * `topicPairs` carries the whole stack; an em dash here means the row has
   * no primary pair, which is not the same as carrying no pairs.
   */
  topic: string
  subtopic: string
  /**
   * Every topic/subtopic pair the row's approaches carry, in approach order
   * (LG-362 §1) — what the column stacks, the topic filter folds over, and
   * the topic sort keys on.
   *
   * Optional for one reason only: a mock row states its single pair in the
   * two flattened fields above and `topicPairsOf` reads it from there. Every
   * builder over real data sets this explicitly, `[]` included — an absent
   * array there would make `topicPairsOf` fabricate a pair out of whatever
   * display strings the flattened fields hold, and that pair would then sort
   * and match as if it were taxonomy.
   */
  topicPairs?: TopicPair[]
  difficulty: Difficulty
  stage: string
  est: number
  // Rounded for display, sorting, filtering, and band shading. Never feed
  // this to the estimate engine — `eloRating` below is the exact value the
  // server derives from, and 56% of the catalog has a fractional rating.
  elo: number | null
  // Exact `Problem.eloRating` (doublePrecision), unrounded, so a client-side
  // derived estimate matches the one the server computes for the same row.
  eloRating?: number | null
  extId: string | null
  source?: string
  /**
   * Every live source this problem can be assigned under, owning first —
   * the Source column's stack and per-source add (LG-362 §2). Absent for
   * mock-mode rows and non-problem kinds, which keep the text-only cell.
   */
  sources?: SourceOption[]
  plans: string[]
  companies: string[]
  estimatedMinutes?: number | null
  learningItemId?: string
  // True when the engagement already has an active incomplete assignment
  // (planned/in_progress/submitted) for this learning item — staging is
  // blocked until that assignment is completed or cancelled.
  alreadyAssigned?: boolean
  catalogKind?:
    | 'problem'
    | 'coding_exercise'
    | 'system_design'
    | 'task'
    | 'source_content'
    | 'resource'
    | 'behavioral'
  catalogId?: string
  repeat?: string
  fit?: string
  rationale?: string
  /**
   * Merged from the per-client seen overlay (LG-362 §8): whether this client
   * has seen the problem, when, and which source produced that date. Absent
   * on mock rows, whose `repeat` text stands in (see `itemIsSeen`).
   * `seenVia` names the duplicate twin the shown sighting happened on
   * (LG-442); null when it is this problem's own.
   */
  seen?: boolean
  lastSeenAt?: Date | null
  seenProvenance?: AttemptProvenance
  seenVia?: ViaAttribution | null
}

export type FilterField =
  | 'topic'
  | 'elo'
  | 'extId'
  | 'plan'
  | 'difficulty'
  | 'company'
  | 'title'
  | 'source'

export interface FilterRule {
  connector: 'where' | 'and' | 'or'
  field: FilterField
  condition: string
  value: string | number | string[]
}

export interface FilterGroup {
  connector: 'and' | 'or'
  rules: FilterRule[]
}

export type FilterNode = FilterRule | FilterGroup

export const condsByKind: Record<
  'text' | 'number' | 'plan' | 'enum' | 'company' | 'source',
  string[]
> = {
  text: ['contains', 'is', 'is not'],
  number: ['>', '<', '=', '≥', '≤'],
  plan: ['in plan', 'not in plan'],
  enum: ['is', 'is not'],
  company: ['asked at', 'not asked at'],
  source: ['is', 'is not'],
}

const lower = (value: string): string => value.toLowerCase()

/**
 * The pairs a row is filtered, sorted, and rendered on — one source of truth
 * so the column can never show a pair the filter did not consider. A mock row
 * carries no `topicPairs` and stands on its single flattened pair; a persisted
 * row always carries the array, empty when the problem is untagged.
 */
export const topicPairsOf = (item: LibraryItem): TopicPair[] =>
  item.topicPairs ?? [
    // A mock row's single pair stands in for its whole taxonomy, so it is by
    // definition that row's primary one — without this every mock row would
    // sort and read as having no primary pair.
    { topic: item.topic, subtopic: item.subtopic, isPrimary: true },
  ]

/**
 * The row's primary pair, or null when it has none — an untagged row, or one
 * whose first approach is untagged. The single place the marker is read, so
 * "which pair speaks for this row" is answered identically by the sort, the
 * meta line, and anything added later.
 */
export const primaryTopicPairOf = (item: LibraryItem): TopicPair | null =>
  topicPairsOf(item).find((pair) => pair.isPrimary) ?? null

/** The composed cell text for one pair — the filters match against this. */
export const topicPairLabel = (pair: TopicPair): string =>
  `${pair.topic} / ${pair.subtopic}`

const selectedStrings = (value: FilterRule['value']): string[] | undefined => {
  if (typeof value === 'string') return [lower(value)]
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === 'string')
  ) {
    return value.map(lower)
  }
  return undefined
}

const effectiveTags = (values: string[]): string[] =>
  (values.length === 0 ? ['General'] : values).map(lower)

const matchesSelection = (values: string[], selection: string[]): boolean =>
  values.some((value) => selection.includes(value))

const evaluateText = (actual: string, rule: FilterRule): boolean => {
  if (typeof rule.value !== 'string') return false

  const haystack = lower(actual)
  const needle = lower(rule.value)
  if (rule.condition === 'contains') return haystack.includes(needle)
  if (rule.condition === 'is') return haystack === needle
  if (rule.condition === 'is not') return haystack !== needle
  return false
}

const evaluateNumber = (actual: number | null, rule: FilterRule): boolean => {
  if (actual === null) return false
  if (
    (typeof rule.value !== 'number' && typeof rule.value !== 'string') ||
    (typeof rule.value === 'string' && rule.value.trim() === '')
  ) {
    return false
  }

  const expected = Number(rule.value)
  if (!Number.isFinite(expected)) return false

  if (rule.condition === '>') return actual > expected
  if (rule.condition === '<') return actual < expected
  if (rule.condition === '=') return actual === expected
  if (rule.condition === '≥') return actual >= expected
  if (rule.condition === '≤') return actual <= expected
  return false
}

const evaluateTags = (
  actual: string[],
  rule: FilterRule,
  positiveCondition: string,
  negativeCondition: string,
): boolean => {
  const selection = selectedStrings(rule.value)
  if (!selection) return false

  const matches = matchesSelection(effectiveTags(actual), selection)
  if (rule.condition === positiveCondition) return matches
  if (rule.condition === negativeCondition) return !matches
  return false
}

const evaluateRule = (rule: FilterRule, item: LibraryItem): boolean => {
  if (rule.field === 'topic') {
    // Any-pair semantics, mirroring `topicCondition` in browse-filter-sql.ts
    // exactly (LG-542): a multi-approach problem matches when ANY of its
    // pairs does, `is not` included. An untagged persisted row has no pair
    // and matches no topic rule in either direction.
    return topicPairsOf(item).some((pair) =>
      evaluateText(topicPairLabel(pair), rule),
    )
  }
  if (rule.field === 'extId') return evaluateText(item.extId ?? '', rule)
  if (rule.field === 'title') return evaluateText(item.title, rule)
  if (rule.field === 'source') return evaluateText(item.source ?? '', rule)
  if (rule.field === 'elo') return evaluateNumber(item.elo, rule)
  if (rule.field === 'plan') {
    return evaluateTags(item.plans, rule, 'in plan', 'not in plan')
  }
  if (rule.field === 'company') {
    return evaluateTags(item.companies, rule, 'asked at', 'not asked at')
  }
  if (rule.field === 'difficulty') {
    const selection = selectedStrings(rule.value)
    if (!selection) return false
    const matches = selection.includes(lower(item.difficulty))
    if (rule.condition === 'is') return matches
    if (rule.condition === 'is not') return !matches
    return false
  }
  return false
}

/**
 * Which condition vocabulary each field uses — the ONE field→kind map.
 * The filter panel derives its choice definitions from this, and
 * `isIncompleteRule` below keys off it, so a newly added field cannot ship
 * a blank default the engine fails to classify (the LG-827 regression:
 * a fresh blank rule evaluating false and blanking the Sources tree).
 */
export const fieldKinds: Record<FilterField, keyof typeof condsByKind> = {
  topic: 'text',
  elo: 'number',
  extId: 'text',
  plan: 'plan',
  difficulty: 'enum',
  company: 'company',
  title: 'text',
  source: 'source',
}

// A rule whose value the coach has cleared (or not yet typed) mid-edit is
// incomplete. It must drop out of the fold entirely — evaluating it (false)
// would empty the table under "and", and treating it as true would match
// everything under "or". A blank string means incomplete for EVERY kind:
// text-like rules ship blank from appendDefaultFilterRule, and selection
// kinds ship blank whenever their option list is empty or not yet loaded
// (`choice()` falls back to `defaultValue: ''`) — either way the value is
// a sentinel, not a choice, and must never blank the Sources tree (LG-827).
// The non-string clause stays scoped to the string/number kinds, where a
// non-string value can only be mid-edit residue; selection kinds carry
// arrays legitimately.
const isIncompleteRule = (rule: FilterRule): boolean => {
  if (typeof rule.value === 'string' && rule.value.trim() === '') return true
  // The array spelling of the same sentinel: an all-cleared multi-select.
  if (Array.isArray(rule.value) && rule.value.length === 0) return true
  const kind = fieldKinds[rule.field]
  if (kind !== 'number' && kind !== 'text' && kind !== 'source') return false
  return typeof rule.value !== 'string' && typeof rule.value !== 'number'
}

/**
 * Whether any rule is complete enough to evaluate — the signal consumers
 * use to decide if filtering is in effect at all. The Sources tree keys its
 * pruning and auto-expand on this instead of `nodes.length`, so a freshly
 * added blank rule neither narrows the tree nor springs it open.
 */
export const hasActiveFilterRules = (nodes: FilterNode[]): boolean =>
  nodes.some((node) =>
    'rules' in node
      ? node.rules.some((rule) => !isIncompleteRule(rule))
      : !isIncompleteRule(node),
  )

const leftFoldRules = (rules: FilterRule[], item: LibraryItem): boolean => {
  const active = rules.filter((rule) => !isIncompleteRule(rule))
  if (active.length === 0) return true

  let result = evaluateRule(active[0], item)
  for (const rule of active.slice(1)) {
    const next = evaluateRule(rule, item)
    result = rule.connector === 'or' ? result || next : result && next
  }
  return result
}

export function evalFilters(nodes: FilterNode[], item: LibraryItem): boolean {
  // Skip incomplete rule nodes and groups whose rules are ALL incomplete,
  // so they neither exclude everything nor match everything mid-edit.
  // (A literally-empty group keeps its established evaluate-to-true role.)
  const active = nodes.filter((node) =>
    'rules' in node
      ? node.rules.length === 0 ||
        node.rules.some((rule) => !isIncompleteRule(rule))
      : !isIncompleteRule(node),
  )
  if (active.length === 0) return true

  const evaluateNode = (node: FilterNode): boolean =>
    'rules' in node ? leftFoldRules(node.rules, item) : evaluateRule(node, item)

  let result = evaluateNode(active[0])
  for (const node of active.slice(1)) {
    const next = evaluateNode(node)
    result = node.connector === 'or' ? result || next : result && next
  }
  return result
}

export function evalCompanyTags(
  tags: { company: string; mode: 'include' | 'exclude' }[],
  item: LibraryItem,
): boolean {
  const companies = item.companies.map(lower)
  const excludes = tags
    .filter((tag) => tag.mode === 'exclude')
    .map((tag) => lower(tag.company))
  if (companies.some((company) => excludes.includes(company))) return false

  const includes = tags
    .filter((tag) => tag.mode === 'include')
    .map((tag) => lower(tag.company))
  if (includes.length === 0) return true
  return companies.some((company) => includes.includes(company))
}
